import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import { fetchAuthIssues } from '../services/reportsService';
import { useError } from '../contexts/ErrorContext';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, instantToWallTime } from '../utils/timezone';
import { getSourceColor, LOGIN_ATTEMPTS_LINE_COLOR } from '../constants/authIssueSources';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import { sortVersionsDesc } from '../utils/versionSort';
import './AuthIssuesReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

// Fixed copy, known before the first fetch — matches backend/reports/auth_issues/index.js.
// Rendered immediately (not gated on `data`) so the banner and coverage notes
// are visible on first load, per eng-plan §4.1 / milestone 4.
const FALLBACK_ANDROID_BANNER = 'Android auth issues are not captured in this report yet — iOS only.';
const FALLBACK_COVERAGE_GAPS = [
  {
    label: 'Buddy-document decode failures',
    reason: "A friend's profile failing to decode — different call site entirely, never reaches Profile Failed to Decode in the first place. Excluded by design.",
  },
  {
    label: 'Non-decode profile-fetch failures',
    reason: 'Post-Login Profile Fetch Failed rows caused by something other than a decode error (e.g. a network failure) stay Crashlytics-only — no Analytics event fires for those.',
  },
];

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="ai-multiselect" ref={ref}>
      <button className="ai-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`ai-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="ai-multiselect-dropdown">
          <div className="ai-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="ai-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="ai-multiselect-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label, failureSources, selectedSourceKey }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="ai-chart-tooltip">
      <div className="ai-chart-tooltip-date">{label}</div>
      {failureSources.map((s) => {
        const errors = row.bySource?.[s.key]?.errors || 0;
        const dimmed = selectedSourceKey && selectedSourceKey !== s.key;
        return (
          <div key={s.key} className={`ai-chart-tooltip-row ${dimmed ? 'dim' : ''}`}>
            <span className="ai-chart-tooltip-swatch" style={{ background: getSourceColor(s.key) }} />
            <span className="ai-chart-tooltip-label">{s.label}</span>
            <span className="ai-chart-tooltip-value">{errors.toLocaleString()}</span>
          </div>
        );
      })}
      <div className="ai-chart-tooltip-divider" />
      <div className="ai-chart-tooltip-row">
        <span className="ai-chart-tooltip-swatch" style={{ background: LOGIN_ATTEMPTS_LINE_COLOR }} />
        <span className="ai-chart-tooltip-label">Login Attempts</span>
        <span className="ai-chart-tooltip-value">{(row.attempts || 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

// Source + day scope, shown next to the By Error / Top Affected Users
// headings — same convention as Goggles Connectivity's ScopeChips.
const ScopeChips = ({ selectedSource, selectedDay, clearDay }) => {
  if (!selectedSource && !selectedDay) return null;
  return (
    <span className="ai-scope-chips">
      {selectedSource && (
        <span className="ai-scope-chip">
          <span className="ai-scope-chip-swatch" style={{ background: getSourceColor(selectedSource.key) }} />
          {selectedSource.label}
        </span>
      )}
      {selectedDay && (
        <span className="ai-scope-chip">
          {selectedDay}
          <button className="ai-scope-chip-clear" onClick={clearDay} title="Clear day filter">&times;</button>
        </span>
      )}
    </span>
  );
};

const DAY_TIME_FORMAT = { hour: 'numeric', minute: '2-digit' };
const formatDayTimeRange = (s, tz) => {
  if (!s?.first_time) return null;
  const first = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...DAY_TIME_FORMAT }).format(new Date(s.first_time));
  if (!s.last_time || s.last_time === s.first_time) return first;
  const last = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...DAY_TIME_FORMAT }).format(new Date(s.last_time));
  return first === last ? first : `${first}–${last}`;
};

const AuthIssuesReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [allAppVersions, setAllAppVersions] = useState([]);
  const [selectedAppVersions, setSelectedAppVersions] = useState([]);

  const resetFilters = useCallback(() => {
    setAllAppVersions([]);
    setSelectedAppVersions([]);
  }, []);

  const [selectedSourceKey, setSelectedSourceKey] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});

  // Day drill-down — clicking a day on the chart (x-axis label or a bar
  // segment) scopes the By Error / Top Affected Users tables to that one
  // day, same convention as Goggles Connectivity's handleDayClick. The
  // chart itself always shows the full range; only the tables below scope.
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);

  const clearDay = useCallback(() => {
    setSelectedDay(null);
    setDayData(null);
  }, []);

  const handleFetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    setExpandedUsers({});
    setSelectedDay(null);
    setDayData(null);
    try {
      const filters = {
        appVersions: overrides.appVersions ?? selectedAppVersions,
      };
      const result = await fetchAuthIssues(startDate, endDate, addError, filters);
      setData(result);

      if (allAppVersions.length === 0 && result.bySource) {
        const appSet = new Set();
        for (const bucket of Object.values(result.bySource)) {
          for (const r of (bucket.byAppVersion || [])) appSet.add(r.app_version);
        }
        const appVers = sortVersionsDesc([...appSet]);
        if (appVers.length) {
          setAllAppVersions(appVers);
          setSelectedAppVersions(appVers);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, addError, selectedAppVersions, allAppVersions.length]);

  const handleApplyFilters = useCallback(() => {
    if (!data) return;
    if (selectedAppVersions.length === 0) {
      setData(prev => ({
        ...prev,
        sourceTotals: Object.fromEntries(
          (prev.sources || []).map(s => [s.key, { total_errors: 0, total_unique_users: 0 }])
        ),
        dailyBySource: [],
        loginFailureRate: 0,
        bySource: Object.fromEntries(
          (prev.sources || []).map(s => [s.key, { byAppVersion: [], byError: [], errorsByUser: [], topUsers: [] }])
        ),
      }));
      return;
    }
    handleFetch({
      appVersions: selectedAppVersions.length === allAppVersions.length ? [] : selectedAppVersions,
    });
  }, [data, handleFetch, selectedAppVersions, allAppVersions.length]);

  const handleDayClick = useCallback(async (day) => {
    if (!day) return;
    if (selectedDay === day) {
      clearDay();
      return;
    }
    setSelectedDay(day);
    setDayLoading(true);
    try {
      const result = await fetchAuthIssues(day, day, addError, {
        appVersions: selectedAppVersions,
      });
      setDayData(result);
    } catch {
      setDayData(null);
    } finally {
      setDayLoading(false);
    }
  }, [selectedDay, selectedAppVersions, addError, clearDay]);

  const sources = useMemo(() => data?.sources || [], [data]);
  const failureSources = useMemo(() => sources.filter(s => s.kind === 'failure'), [sources]);
  const informationalSources = useMemo(() => sources.filter(s => s.kind === 'informational'), [sources]);
  const selectedSource = sources.find(s => s.key === selectedSourceKey);
  const timezone = data?.timezone || ANALYTICS_TIMEZONE;

  const chartData = useMemo(() => {
    if (!data?.dailyBySource?.length) return [];
    const attemptsByDate = new Map((data.dailyLoginAttempts || []).map(d => [d.date, d.attempts || 0]));
    return data.dailyBySource.map(d => ({
      date: d.date,
      bySource: d.bySource,
      attempts: attemptsByDate.get(d.date) || 0,
    }));
  }, [data]);

  const chartMax = useMemo(() => {
    let max = 0;
    for (const row of chartData) {
      const stackTotal = failureSources.reduce((sum, s) => sum + (row.bySource?.[s.key]?.errors || 0), 0);
      max = Math.max(max, stackTotal, row.attempts || 0);
    }
    return max;
  }, [chartData, failureSources]);

  // Deep-link helpers — same convention as Goggles Connectivity
  // (feedback_analytics_report_conventions.md #4-#5): absolute instants + tz,
  // session-level grouping, day-range fallback for a user with no linkable
  // session (real for every Crashlytics-sourced row, and for a GA4 row whose
  // events carry no ga_session_id anywhere in the range). `endDay` widens the
  // window past `day` when a session's events straddle midnight — the target
  // page has to load every day the session touches to resolve it to the same
  // session id this report sent (see backend/utils/orphanSessions.js).
  const getUserRangeHref = useCallback((uid, day, sessionId = null, endDay = null) => {
    if (!uid || !day) return `/users/${uid}`;
    const start = wallTimeToInstant(`${day}T00:00`, timezone);
    const end = wallTimeToInstant(`${endDay && endDay > day ? endDay : day}T23:59`, timezone);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics', startDate: start.toISOString(), endDate: end.toISOString(), tz: timezone,
    });
    if (sessionId) params.set('session', sessionId);
    return `/users/${uid}?${params.toString()}`;
  }, [timezone]);

  const getUserSessionHref = useCallback((uid, session) => (
    getUserRangeHref(uid, session.day, session.session_id, session.end_day)
  ), [getUserRangeHref]);

  const getUserSpanHref = useCallback((uid, firstSeen, lastSeen) => {
    if (!uid || !firstSeen) return `/users/${uid}`;
    const startDay = instantToWallTime(firstSeen, timezone).slice(0, 10);
    const endDay = instantToWallTime(lastSeen || firstSeen, timezone).slice(0, 10);
    const start = wallTimeToInstant(`${startDay}T00:00`, timezone);
    const end = wallTimeToInstant(`${endDay}T23:59`, timezone);
    if (!start || !end) return `/users/${uid}`;
    const params = new URLSearchParams({
      tab: 'analytics', startDate: start.toISOString(), endDate: end.toISOString(), tz: timezone,
    });
    return `/users/${uid}?${params.toString()}`;
  }, [timezone]);

  // When a day is selected, the By Error / Top Affected Users tables read
  // from that single-day fetch instead of the full-range one — same
  // `scopedSnapshot` convention as Goggles Connectivity. The chart itself
  // (chartData/chartMax above) always stays full-range.
  const scopedSnapshot = (selectedDay && dayData) ? dayData : data;

  // Aggregate across every failure source when none is selected — same
  // pattern as Goggles Connectivity's aggregateByVersion/aggregateUsers.
  // account_revoked (informational) is excluded from the default aggregate;
  // selecting it directly still works, since it's a normal entry in `sources`.
  const aggregateByError = useMemo(() => {
    if (!scopedSnapshot?.bySource) return [];
    const rowsFor = selectedSource ? [selectedSource] : failureSources;
    const out = [];
    for (const s of rowsFor) {
      for (const row of (scopedSnapshot.bySource[s.key]?.byError || [])) {
        out.push({ ...row, source_key: s.key, source_label: s.label });
      }
    }
    return out.sort((a, b) => b.occurrences - a.occurrences);
  }, [scopedSnapshot, selectedSource, failureSources]);

  const aggregateTopUsers = useMemo(() => {
    if (!scopedSnapshot?.bySource) return [];
    const rowsFor = selectedSource ? [selectedSource] : failureSources;
    const out = new Map();
    for (const s of rowsFor) {
      for (const row of (scopedSnapshot.bySource[s.key]?.topUsers || [])) {
        const k = row.user_identifier;
        if (!out.has(k)) {
          out.set(k, {
            user_identifier: row.user_identifier,
            user_id: row.user_id || null,
            user_name: row.user_name || null,
            error_count: 0,
            first_seen: row.first_seen,
            last_seen: row.last_seen,
            app_versions: new Set(),
            device_models: new Set(),
            bySessionMap: new Map(),
          });
        }
        const acc = out.get(k);
        acc.error_count += row.error_count || 0;
        if (row.first_seen && (!acc.first_seen || row.first_seen < acc.first_seen)) acc.first_seen = row.first_seen;
        if (row.last_seen && (!acc.last_seen || row.last_seen > acc.last_seen)) acc.last_seen = row.last_seen;
        for (const v of (row.app_versions || [])) acc.app_versions.add(v);
        for (const m of (row.device_models || [])) acc.device_models.add(m);
        for (const sess of (row.by_session || [])) {
          const existing = acc.bySessionMap.get(sess.session_id);
          if (!existing) acc.bySessionMap.set(sess.session_id, { ...sess });
          else {
            existing.error_count += sess.error_count || 0;
            if (sess.first_time < existing.first_time) existing.first_time = sess.first_time;
            if (sess.last_time > existing.last_time) existing.last_time = sess.last_time;
          }
        }
      }
    }
    return Array.from(out.values())
      .map(r => ({
        ...r,
        app_versions: [...r.app_versions],
        device_models: [...r.device_models],
        by_session: Array.from(r.bySessionMap.values()).sort((a, b) => a.first_time.localeCompare(b.first_time)),
      }))
      .sort((a, b) => b.error_count - a.error_count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedSnapshot, selectedSource, failureSources]);

  const toggleSource = (key) => setSelectedSourceKey(prev => (prev === key ? null : key));

  const androidBanner = data?.androidBanner || FALLBACK_ANDROID_BANNER;
  const coverageGaps = data?.coverageGaps?.length ? data.coverageGaps : FALLBACK_COVERAGE_GAPS;
  const rolloutNotes = data?.rolloutNotes || [];

  return (
    <div className="ai-report">
      <div className="ai-header">
        <div className="ai-header-left">
          <h2 className="ai-title">Auth Issues</h2>
          <p className="ai-subtitle">
            {selectedSource?.description || 'Login, sign-up, account-deletion, and profile-load failures'}
          </p>
        </div>
        <div className="ai-controls">
          <input
            type="date"
            className="ai-date-input"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); resetFilters(); }}
          />
          <span className="ai-date-sep">to</span>
          <input
            type="date"
            className="ai-date-input"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); resetFilters(); }}
          />
          <button className="ai-fetch-btn" onClick={() => handleFetch()} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      {/* Android banner — fixed copy, visible before the first fetch */}
      <div className="ai-android-banner">{androidBanner}</div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {/* Coverage gaps + rollout caveat — visible before the first fetch */}
      <div className="ai-coverage">
        <div className="ai-coverage-list">
          {coverageGaps.map((g) => (
            <div key={g.label} className="ai-coverage-item">
              <span className="ai-coverage-label">{g.label}</span>
              <span className="ai-coverage-reason">{g.reason}</span>
            </div>
          ))}
        </div>
        {rolloutNotes.length > 0 && (
          <div className="ai-rollout-note">
            {rolloutNotes.map((n) => (
              <div key={n.label}>
                <strong>{n.label}:</strong> {n.reason}
              </div>
            ))}
          </div>
        )}
      </div>

      {allAppVersions.length > 0 && (
        <div className="ai-filters">
          <MultiSelect
            label="iOS Version"
            options={allAppVersions}
            selected={selectedAppVersions}
            onChange={setSelectedAppVersions}
          />
          <button className="ai-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="ai-error">{error}</div>}

      {!data && !loading && !error && (
        <div className="ai-empty">Select a date range and click Run Report</div>
      )}

      {loading && (
        <div className="ai-loading">
          <div className="ai-spinner" />
          <p>Querying Analytics and Crashlytics...</p>
        </div>
      )}

      {data && !loading && (
        <div className="ai-content">
          {/* Failure rate + source cards */}
          <div className="ai-source-cards">
            <div className="ai-rate-card">
              <span className="ai-rate-card-value">{(data.loginFailureRate * 100).toFixed(1)}%</span>
              <span className="ai-rate-card-label">Login Failure Rate</span>
            </div>
            {failureSources.map((s) => {
              const totals = data.sourceTotals?.[s.key] || { total_errors: 0, total_unique_users: 0 };
              const isActive = s.key === selectedSourceKey;
              const color = getSourceColor(s.key);
              return (
                <div
                  key={s.key}
                  role="button"
                  tabIndex={0}
                  className={`ai-source-card ${isActive ? 'active' : ''}`}
                  style={{ '--source-color': color }}
                  onClick={() => toggleSource(s.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSource(s.key); } }}
                  title={s.description}
                >
                  <div className="ai-source-card-name">
                    <span className="ai-source-card-swatch" style={{ background: color }} />
                    {s.label}
                  </div>
                  <div className="ai-source-card-stats">
                    <span className="ai-source-card-errors">{totals.total_errors.toLocaleString()}</span>
                    <span className="ai-source-card-users">{totals.total_unique_users.toLocaleString()} unique</span>
                  </div>
                </div>
              );
            })}
            {informationalSources.map((s) => {
              const totals = data.sourceTotals?.[s.key] || { total_errors: 0, total_unique_users: 0 };
              const isActive = s.key === selectedSourceKey;
              const color = getSourceColor(s.key);
              return (
                <div
                  key={s.key}
                  role="button"
                  tabIndex={0}
                  className={`ai-source-card ai-source-card-informational ${isActive ? 'active' : ''}`}
                  style={{ '--source-color': color }}
                  onClick={() => toggleSource(s.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSource(s.key); } }}
                  title={s.description}
                >
                  <div className="ai-source-card-name">
                    <span className="ai-source-card-swatch" style={{ background: color }} />
                    {s.label}
                    <span className="ai-source-card-tag">not a failure</span>
                  </div>
                  <div className="ai-source-card-stats">
                    <span className="ai-source-card-errors">{totals.total_errors.toLocaleString()}</span>
                    <span className="ai-source-card-users">{totals.total_unique_users.toLocaleString()} unique</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Daily chart — failure sources only, stacked, with login-attempt denominator */}
          <div className="ai-chart-section">
            <h3 className="ai-section-title">
              Daily Failures <span className="ai-section-sub">(stacked by source) &middot; Login Attempts (line, success + failure)</span>
            </h3>
            <div className="ai-chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#444"
                    tick={({ x, y, payload }) => (
                      <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => handleDayClick(payload.value)}>
                        <text
                          dy={12}
                          textAnchor="middle"
                          fill={selectedDay === payload.value ? '#fff' : '#999'}
                          fontSize={11}
                          fontWeight={selectedDay === payload.value ? 600 : 400}
                          fontFamily="SF Mono, Monaco, monospace"
                        >
                          {payload.value ? payload.value.slice(5) : ''}
                        </text>
                      </g>
                    )}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#444"
                    allowDecimals={false}
                    domain={[0, chartMax]}
                    tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    content={(props) => <ChartTooltip {...props} failureSources={failureSources} selectedSourceKey={selectedSourceKey} />}
                  />
                  {failureSources.map((s) => (
                    <Bar
                      key={s.key}
                      yAxisId="left"
                      dataKey={(row) => row.bySource?.[s.key]?.errors || 0}
                      stackId="errors"
                      name={s.label}
                      fill={getSourceColor(s.key)}
                      fillOpacity={selectedSourceKey && selectedSourceKey !== s.key ? 0.25 : 1}
                      minPointSize={2}
                      isAnimationActive={false}
                      cursor="pointer"
                      onClick={(barData) => {
                        setSelectedSourceKey(s.key);
                        const day = barData?.payload?.date || barData?.date;
                        if (day) handleDayClick(day);
                      }}
                    />
                  ))}
                  {/* Invisible filler completing each day's stack up to
                      chartMax — a real segment can be only a couple px tall,
                      too thin to reliably click, so this fills the rest of
                      that day's column with a click target that just drills
                      into the day (leaves source selection alone). Same
                      pattern as Goggles Connectivity's day-click filler. */}
                  <Bar
                    yAxisId="left"
                    dataKey={(row) => {
                      const stackTotal = failureSources.reduce((sum, s) => sum + (row.bySource?.[s.key]?.errors || 0), 0);
                      return Math.max(0, chartMax - stackTotal);
                    }}
                    stackId="errors"
                    fill="transparent"
                    isAnimationActive={false}
                    cursor="pointer"
                    onClick={(barData) => {
                      const day = barData?.payload?.date || barData?.date;
                      if (day) handleDayClick(day);
                    }}
                  />
                  <Line
                    yAxisId="left"
                    dataKey="attempts"
                    stroke={LOGIN_ATTEMPTS_LINE_COLOR}
                    strokeWidth={2.5}
                    dot={{ stroke: LOGIN_ATTEMPTS_LINE_COLOR, strokeWidth: 0, r: 3, fill: LOGIN_ATTEMPTS_LINE_COLOR }}
                    activeDot={{ stroke: LOGIN_ATTEMPTS_LINE_COLOR, strokeWidth: 0, r: 4.5, fill: LOGIN_ATTEMPTS_LINE_COLOR }}
                    isAnimationActive={false}
                    name="Login Attempts"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* By-error breakdown */}
          <div className="ai-table-section">
            <h3 className="ai-section-title">
              By Error <ScopeChips selectedSource={selectedSource} selectedDay={selectedDay} clearDay={clearDay} />
            </h3>
            <div className="ai-table">
              <div className="ai-table-header ai-table-header-error">
                <span>Source</span>
                <span>Code / Reason</span>
                <span>Meaning</span>
                <span>Occurrences</span>
                <span>Users</span>
                <span>Last Seen</span>
              </div>
              <div className="ai-table-scroll">
                {dayLoading ? (
                  <div className="ai-table-empty">Loading...</div>
                ) : aggregateByError.map((row, i) => (
                  <div key={`${row.source_key}-${row.error_code}-${row.error_text}-${i}`} className="ai-table-row ai-table-row-error">
                    <span className="ai-table-cell-source">
                      <span className="ai-table-cell-swatch" style={{ background: getSourceColor(row.source_key) }} />
                      {row.source_label}
                    </span>
                    <span className="ai-table-cell-code">
                      {row.error_code
                        ? <code>{row.error_code}</code>
                        : <span className="ai-table-cell-text-tag">by message</span>}
                      <span className="ai-table-cell-text">{row.error_text || '—'}</span>
                    </span>
                    <span>{row.meaning}</span>
                    <span>{row.occurrences.toLocaleString()}</span>
                    <span>{row.unique_users.toLocaleString()}</span>
                    <span className="ai-table-cell-muted">{row.last_seen}</span>
                  </div>
                ))}
                {aggregateByError.length === 0 && (
                  <div className="ai-table-empty">No errors in this range.</div>
                )}
              </div>
            </div>
          </div>

          {/* Top affected users */}
          <div className="ai-table-section">
            <h3 className="ai-section-title">
              Top Affected Users <ScopeChips selectedSource={selectedSource} selectedDay={selectedDay} clearDay={clearDay} />
            </h3>
            <div className="ai-table ai-table-wide">
              <div className="ai-table-header ai-table-header-users">
                <span>User</span>
                <span>Errors</span>
                <span>Devices</span>
                <span>App Versions</span>
                <span>Last Seen</span>
                <span></span>
              </div>
              {dayLoading ? (
                <div className="ai-table-empty">Loading...</div>
              ) : aggregateTopUsers.slice(0, 20).map(user => {
                const sessions = user.by_session || [];
                const hasUid = !!user.user_id;
                const isMultiSession = hasUid && sessions.length > 1;
                const isExpanded = !!expandedUsers[user.user_identifier];
                const singleHref = !hasUid
                  ? null
                  : sessions.length === 1
                    ? getUserSessionHref(user.user_id, sessions[0])
                    : sessions.length === 0
                      ? getUserSpanHref(user.user_id, user.first_seen, user.last_seen)
                      : null;

                const toggleExpand = () => setExpandedUsers(prev => ({ ...prev, [user.user_identifier]: !prev[user.user_identifier] }));

                const RowTag = isMultiSession ? 'div' : 'a';
                const rowProps = isMultiSession
                  ? {
                      role: 'button', tabIndex: 0, onClick: toggleExpand,
                      onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(); } },
                    }
                  : { href: singleHref || null, target: '_blank', rel: 'noopener noreferrer' };

                return (
                  <React.Fragment key={user.user_identifier}>
                    <RowTag
                      className={`ai-table-row ai-table-row-users ${hasUid ? 'ai-table-row-clickable' : ''} ${isMultiSession ? 'ai-table-row-expandable' : ''}`}
                      {...rowProps}
                    >
                      <span className="ai-table-cell-primary ai-user-name-cell">
                        {isMultiSession && <span className={`ai-expand-chevron ${isExpanded ? 'open' : ''}`}>&#9656;</span>}
                        {user.user_name || user.user_id || user.user_identifier.substring(0, 12) + '...'}
                      </span>
                      <span>
                        {user.error_count.toLocaleString()}
                        {isMultiSession && <span className="ai-table-cell-muted"> &middot; {sessions.length} sessions</span>}
                      </span>
                      <span>{user.device_models.join(', ') || '—'}</span>
                      <span>{user.app_versions.join(', ') || '—'}</span>
                      <span className="ai-table-cell-muted">{new Date(user.last_seen).toLocaleDateString()}</span>
                      <span className="ai-copy-cell">
                        {user.user_id && (
                          <button
                            className="ai-copy-btn"
                            title="Copy user ID"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(user.user_id); }}
                          >
                            &#x1F4CB;
                          </button>
                        )}
                      </span>
                    </RowTag>
                    {isMultiSession && isExpanded && (
                      <div className="ai-table-row-sessions">
                        {sessions.slice().reverse().map(s => (
                          <a
                            key={s.session_id}
                            href={getUserSessionHref(user.user_id, s)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ai-table-row-session"
                          >
                            <span className="ai-table-row-session-date">
                              {s.day}
                              <span className="ai-table-row-session-time">{formatDayTimeRange(s, timezone)}</span>
                            </span>
                            <span className="ai-table-row-session-count">
                              {s.error_count.toLocaleString()} {s.error_count === 1 ? 'error' : 'errors'}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {aggregateTopUsers.length === 0 && (
                <div className="ai-table-empty">No affected users in this range.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthIssuesReport;
